import type { BillingPaymentMethod } from '@saaga/shared/interfaces/billing.interface'
import { selectSubscriptionInfo, selectTenant } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { useMemo } from 'react'

import type { BillingPaymentCard, BillingPlan } from '@/components/billing/billing.interface'
import { toCardBrand } from '@/utils/card-brand.util'

export interface SubscriptionState {
  planName: string | null
  status: string | null
  isCancelling: boolean
  cancelAt: string | null
  renewalAt: string | null
  /**
   * Whether paid subscriptions are enabled (backend flag). When false the
   * subscription-settings UI shows the Early Access banner and disables
   * the subscribe/cancel CTAs. Defaults to `true` to keep the UI open
   * when billing hasn't loaded yet.
   */
  subscriptionsEnabled: boolean
}

export interface UseSubscriptionStateReturn {
  subscription: SubscriptionState
  defaultCard: BillingPaymentCard | null
  isCurrentPlan: (plan: BillingPlan) => boolean
  isFreePlan: boolean
}

const mapPaymentMethodToCard = (pm: BillingPaymentMethod | null): BillingPaymentCard | null => {
  if (!pm?.cardLast4) return null

  return {
    id: pm.id,
    brand: toCardBrand(pm.cardBrand),
    last4: pm.cardLast4,
    expMonth: pm.cardExpMonth ?? 0,
    expYear: pm.cardExpYear ?? 0,
    isDefault: true,
  }
}

const buildSubscriptionState = (
  info: {
    planName: string | null
    status: string | null
    isCancelling: boolean
    subscriptionsEnabled: boolean
  } | null,
  cancelAt: string | null,
  renewalAt: string | null
): SubscriptionState => ({
  planName: info?.planName ?? null,
  status: info?.status ?? null,
  isCancelling: info?.isCancelling ?? false,
  cancelAt,
  renewalAt,
  subscriptionsEnabled: info?.subscriptionsEnabled ?? true,
})

export const useSubscriptionState = (): UseSubscriptionStateReturn => {
  const tenant = useAppSelector(selectTenant)
  const subscriptionInfo = useAppSelector(selectSubscriptionInfo)

  const subscription = useMemo(
    () =>
      buildSubscriptionState(
        subscriptionInfo,
        tenant?.subscriptionCancelAt ?? null,
        tenant?.subscriptionRenewalAt ?? null
      ),
    [subscriptionInfo, tenant?.subscriptionCancelAt, tenant?.subscriptionRenewalAt]
  )

  const defaultCard = useMemo(
    () => mapPaymentMethodToCard(tenant?.defaultPaymentMethod ?? null),
    [tenant?.defaultPaymentMethod]
  )

  const isCurrentPlan = (plan: BillingPlan): boolean =>
    plan.name.toLowerCase() === subscription.planName?.toLowerCase()

  const isFreePlan = subscription.planName?.toLowerCase() === 'free'

  return {
    subscription,
    defaultCard,
    isCurrentPlan,
    isFreePlan,
  }
}
